import {gql} from '@apollo/client';
import {entry, objectFromEntries} from '@benchling/js-utils';
import React from 'react';

import {StorableSummary, StorableWithPermissions} from 'constants/SampleConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {assertAllAccessible} from 'graphql-client/InaccessibleInfo';
import {
  nullableRESTStorableSummaryToGraphQL,
  restStorableSummaryToGraphQL,
} from 'samples/storables/graphql/translateStorable';
import {
  getInformationForMoveStorablesAction,
  getMoveStorablesTooltip,
  shouldDisableMoveStorablesAction,
} from 'samples/storables/MoveStorablesUtil';
import {PlateSchemaType} from 'samples/storables/StorableSchemaConstants';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import APIUtil from 'util/APIUtil';

import {openMoveStorablesModal} from '../MoveStorablesModal';
import {
  MoveStorablesButtonQuery,
  MoveStorablesButtonQueryVariables,
} from './__generated/MoveStorablesButtonQuery';

function handleOpenMoveStorables(
  storables: Array<StorableWithPermissions>,
  defaultParentStorable?: StorableSummary
): void {
  openMoveStorablesModal({
    storables: storables.map((s) => restStorableSummaryToGraphQL(s)),
    preselectedParentStorable: nullableRESTStorableSummaryToGraphQL(defaultParentStorable) ?? undefined,
  });
}

interface Props {
  storables: Array<StorableWithPermissions>;
  buttonStyle: ButtonStyle;
  forceDisabled?: boolean;
  customTooltipText?: string;
  defaultParentStorable?: StorableSummary;
  // This callback aims to track user clicks.
  onClickTracking?: () => void;
}

interface LoadedProps {
  plateTypeById: {[parentPlateId: string]: PlateSchemaType};
}

function MoveStorablesButton(props: Props & LoadedProps): JSX.Element {
  const {someStorablesAreSpecified, allStorablesAreWritable, noFixedPlateWellsSpecified} =
    getInformationForMoveStorablesAction(props.storables, props.plateTypeById);
  const disabled =
    props.forceDisabled ||
    shouldDisableMoveStorablesAction(
      someStorablesAreSpecified,
      allStorablesAreWritable,
      noFixedPlateWellsSpecified
    );

  return (
    <Tooltip
      title={getMoveStorablesTooltip(
        someStorablesAreSpecified,
        allStorablesAreWritable,
        noFixedPlateWellsSpecified,
        props.customTooltipText
      )}
    >
      <Button
        aria-label="Move to location"
        buttonStyle={props.buttonStyle}
        disabled={disabled}
        icon="move-box"
        onClick={() => {
          props.onClickTracking?.();
          handleOpenMoveStorables(props.storables, props.defaultParentStorable);
        }}
        size={ButtonSize.SMALL}
        dataTestAttrs={{component: 'MoveStorablesButton', element: 'button'}}
      />
    </Tooltip>
  );
}

// We don't want to fetch the full plates because we don't need all the cells and tags.
export const MOVE_STORABLES_BUTTON_QUERY = gql`
  query MoveStorablesButtonQuery($parentPlateIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: INVENTORY) {
    plates(ids: $parentPlateIds) {
      ... on PlateNode {
        id
        storableSchema {
          id
          ... on PlateSchemaNode {
            id
            plateSchemaType
          }
        }
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function QueryingMoveStorablesButton(props: Props): JSX.Element {
  if (props.storables == null || props.storables.length === 0) {
    return <MoveStorablesButton {...props} plateTypeById={{}} />;
  }

  const uniqueParentPlateIds = [
    ...new Set(
      props.storables.flatMap(({parentStorableId}) =>
        parentStorableId != null && APIUtil.isPlate(parentStorableId) ? [parentStorableId] : []
      )
    ),
  ];

  return (
    <BenchlingQuery<MoveStorablesButtonQuery, MoveStorablesButtonQueryVariables>
      query={MOVE_STORABLES_BUTTON_QUERY}
      variables={{parentPlateIds: uniqueParentPlateIds}}
      renderLoading={() => null}
    >
      {(data) => {
        const accessiblePlates = assertAllAccessible(data.plates);
        const plateTypeById = objectFromEntries(
          accessiblePlates.map((plate) => entry(plate.id, plate.storableSchema.plateSchemaType))
        );
        return <MoveStorablesButton {...props} plateTypeById={plateTypeById} />;
      }}
    </BenchlingQuery>
  );
}

export default QueryingMoveStorablesButton;
